import React from 'react';
import clsx from 'clsx';

interface SkeletonClientItemProps {
  viewMode: 'grid' | 'table';
  count?: number;
  columns?: string; 
}

const getGridClasses = (columns: string): string => {
  const parts = columns.split(' ').map((part) => {
    const [breakpoint, cols] = part.includes(':') ? part.split(':') : ['', part];
    const prefix = breakpoint ? `${breakpoint}:grid-cols-${cols}` : `grid-cols-${cols}`;
    return prefix;
  });
  return parts.join(' ');
};

const SkeletonClientItem: React.FC<SkeletonClientItemProps> = ({ 
  viewMode, 
  count = 3, 
  columns = "1 sm:2 lg:3" 
}) => {
  if (viewMode === 'grid') {
    return (
      <div className={clsx("grid", getGridClasses(columns), "gap-4")}>
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className="bg-primary-50 dark:bg-primary-900 border border-gray-200 dark:border-gray-700 
                       rounded-xl p-6 shadow-md animate-fadePulse"
            style={{ animationDelay: `${index * 100}ms` }} 
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full animate-fadePulse" />
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 animate-fadePulse" />
              </div>
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16 animate-fadePulse" />
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-fadePulse" />
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/3 animate-fadePulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="animate-fadePulse">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={clsx(
            'flex items-center h-16 border-b border-primary-200 dark:border-primary-700',
            index % 2 === 0 ? 'bg-primary-50 dark:bg-primary-900' : 'bg-primary-100 dark:bg-primary-800'
          )}
        >
          <div className="w-8 px-6">
            <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded animate-fadePulse" />
          </div>
          <div className="flex-1 px-6 py-4">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-fadePulse" />
          </div>
          <div className="w-32 px-6 py-4">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 animate-fadePulse" />
          </div>
          <div className="w-24 px-6 py-4">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-fadePulse" />
          </div>
          <div className="w-24 px-6 py-4">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-fadePulse" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default SkeletonClientItem;